import ky, { HTTPError } from 'lib/ky'

export default class Resource {
  constructor({ attrs = {}, parent, root, resourcePath }) {
    this.data = attrs
    this.proxy = new Proxy(this.data, {
      get: this.proxyGet.bind(this),
      set: this.proxySet.bind(this),
    })

    this.object = this
    this._parent = parent
    this._root = root
    this._resourcePath = resourcePath
    this.errors = {}
    this.details = {}

    Object.keys(attrs).forEach((attr) => {
      this.proxy[attr] = attrs[attr]
    })

    return this.proxy
  }

  proxyGet(data, prop) {
    if (prop in this.object && this.object[prop] !== undefined) {
      return this.object[prop]
    }

    return data[prop]
  }

  proxySet(data, prop, value) {
    // TODO: __lookupSetter__
    if (prop in this.object) {
      this.object[prop] = value
    }
    else {
      data[prop] = value
    }

    return true
  }

  setAttrs(attrs = {}) {
    Object.keys(attrs).forEach((key) => {
      this.data[key] = attrs[key]
    })

    return this
  }

  get params() {
    return { root: this.root, resourcePath: this.resourcePath }
  }

  get root() {
    if (this._root) return this._root
    if (this._parent) this._root = `${this._parent.resourcePath}/${this.constructor.paramName}s`

    return this._root
  }

  set root(root) {
    this._root = root
  }

  get resourcePath() {
    if (this._resourcePath) {
      return this._resourcePath
    }
    else if (this.data.id) {
      return `${this.root}/${this.data.id}`
    }

    return null
  }

  get collectionPath() {
    return this.resourcePath || `${this.root}`
  }

  get pushPath() {
    return this.resourcePath || this.collectionPath
  }

  get pushMethod() {
    return this.data.id ? 'put' : 'post'
  }

  get asJson() {
    return Object.assign({}, this.data)
  }

  get hasErrors() {
    return Object.keys(this.errors).length > 0
  }

  get fullErrorMessages() {
    return Object.values(this.errors).join('; ')
  }

  get isPersisted() {
    return typeof this.id === 'number'
  }

  // #private
  buildParamsToPush({ params, pushOnly }) {
    let json = {}

    if (pushOnly) {
      pushOnly.forEach((attr) => {
        json[attr] = this.data[attr]
      })
    }
    else {
      json = this.asJson
    }

    params[this.constructor.paramName]
      = Object.assign(json, params[this.constructor.paramName])

    return params
  }

  // params - request params
  // only - Array
  async push({ params = {}, pushOnly = null, retrieveOnly = null, only = null } = {}) {
    if (typeof this.constructor.paramName === 'undefined') {
      throw new Error(`paramName not set for ${this.constructor.name}`)
    }

    params = this.buildParamsToPush({ params, pushOnly: pushOnly || only })

    try {
      const response = await ky[this.pushMethod](`${this.pushPath}.json`, { json: params }).json()

      if (retrieveOnly || only) {
        this.errors = {} // needs to be cleared because it can be moved
        this.details = {}
        this.setAttrs(this.constructor.filterResponse({ response, only: retrieveOnly || only }))
        return this
      }
      else {
        return new this.constructor({ attrs: response, ...this.params })
      }
    }
    catch (e) {
      return await this.handlePushError(e)
    }
  }

  async handlePushError(e) {
    if (e instanceof HTTPError) {
      const response = await e.response.json()

      if (response.errors !== undefined) {
        this.errors = response.errors
        this.details = response.details
        return this
      }
      else {
        throw e
      }
    }
    else {
      throw e
    }
  }

  async retrieve(options = {}) {
    return new this.constructor(
      { attrs: await ky.get(`${this.resourcePath}.json`).json(), ...this.params, ...options },
    )
  }

  async delete() {
    return new this.constructor(
      { attrs: await ky.delete(`${this.resourcePath}.json`).json(), ...this.params },
    )
  }

  static async retrieve({ root, resourcePath, options }) {
    return new this(
      { attrs: await ky.get(`${resourcePath}.json`).json(), root, resourcePath, ...options },
    )
  }

  static filterResponse({ response, only }) {
    const filtered = {}
    only.forEach((attr) => {
      filtered[attr] = response[attr]
    })
    return filtered
  }
}
